import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import {
  Shield,
  Search,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Globe,
  Activity,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X
} from 'lucide-react';

const SecurityAuditLogs = ({ superAdminToken }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    eventCategory: '',
    severity: '',
    success: '',
    userEmail: '',
    ipAddress: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  
  // View mode
  const [viewMode, setViewMode] = useState('logs'); // 'logs' or 'dashboard'
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', `${filters.startDate}T00:00:00Z`);
      if (filters.endDate) params.append('end_date', `${filters.endDate}T23:59:59Z`);
      if (filters.eventCategory) params.append('event_category', filters.eventCategory);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.success !== '') params.append('success', filters.success);
      if (filters.userEmail) params.append('user_email', filters.userEmail);
      if (filters.ipAddress) params.append('ip_address', filters.ipAddress);
      params.append('limit', limit);
      params.append('offset', page * limit);
      
      const response = await api.get(`/super-admin/security-audit-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [superAdminToken, filters, page, limit]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', `${filters.startDate}T00:00:00Z`);
      if (filters.endDate) params.append('end_date', `${filters.endDate}T23:59:59Z`);
      
      const response = await api.get(`/super-admin/security-audit-logs/summary?${params}`, {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [superAdminToken, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [fetchLogs, fetchSummary]);


  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.append('start_date', `${filters.startDate}T00:00:00Z`);
      params.append('end_date', `${filters.endDate}T23:59:59Z`);
      if (filters.eventCategory) params.append('event_category', filters.eventCategory);
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${baseUrl}/api/super-admin/security-audit-logs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to export');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security_audit_logs_${filters.startDate}_${filters.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError('Failed to export logs');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
      case 'error': return { backgroundColor: '#ffedd5', color: '#9a3412', borderColor: '#fed7aa' };
      case 'warning': return { backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fef08a' };
      default: return { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#bfdbfe' };
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
      case 'error':
        return <AlertTriangle size={14} />;
      case 'warning':
        return <Clock size={14} />;
      default:
        return <Activity size={14} />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'authentication': return { backgroundColor: '#f3e8ff', color: '#7c3aed' };
      case 'authorization': return { backgroundColor: '#dbeafe', color: '#2563eb' };
      case 'data_access': return { backgroundColor: '#dcfce7', color: '#16a34a' };
      case 'data_modification': return { backgroundColor: '#ffedd5', color: '#ea580c' };
      case 'security': return { backgroundColor: '#fee2e2', color: '#dc2626' };
      case 'system': return { backgroundColor: '#f3f4f6', color: '#4b5563' };
      default: return { backgroundColor: '#f3f4f6', color: '#4b5563' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const totalPages = Math.ceil(total / limit);

  const styles = {
    container: {
      padding: '1.5rem',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1e293b'
    },
    headerActions: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e2e8f0',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    buttonPrimary: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    statCard: {
      backgroundColor: 'white',
      padding: '1.25rem',
      borderRadius: '0.75rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#1e293b'
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#64748b',
      marginTop: '0.25rem'
    },
    filtersPanel: {
      backgroundColor: 'white',
      padding: '1rem',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '1rem'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    filterLabel: {
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase'
    },
    filterInput: {
      padding: '0.5rem',
      borderRadius: '0.375rem',
      border: '1px solid #e2e8f0',
      fontSize: '0.875rem'
    },
    table: {
      width: '100%',
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    th: {
      padding: '0.75rem 1rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase',
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0'
    },
    td: {
      padding: '0.75rem 1rem',
      fontSize: '0.875rem',
      borderBottom: '1px solid #f1f5f9'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      backgroundColor: 'white',
      borderTop: '1px solid #e2e8f0'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
      padding: '1.5rem'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <Shield size={28} style={{ color: '#3b82f6' }} />
          <span>Security Audit Logs</span>
        </div>
        <div style={styles.headerActions}>
          <button
            style={{
              ...styles.button,
              ...(viewMode === 'dashboard' ? styles.buttonPrimary : {})
            }}
            onClick={() => setViewMode('dashboard')}
          >
            <Activity size={16} />
            Dashboard
          </button>
          <button
            style={{
              ...styles.button,
              ...(viewMode === 'logs' ? styles.buttonPrimary : {})
            }}
            onClick={() => setViewMode('logs')}
          >
            <Search size={16} />
            Logs
          </button>
          <button
            style={styles.button}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            style={styles.button}
            onClick={handleExport}
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            style={styles.button}
            onClick={() => { fetchLogs(); fetchSummary(); }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={styles.filtersPanel}>
          <div style={styles.filterGrid}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Start Date</label>
              <input
                type="date"
                style={styles.filterInput}
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>End Date</label>
              <input
                type="date"
                style={styles.filterInput}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Category</label>
              <select
                style={styles.filterInput}
                value={filters.eventCategory}
                onChange={(e) => setFilters({ ...filters, eventCategory: e.target.value })}
              >
                <option value="">All Categories</option>
                <option value="authentication">Authentication</option>
                <option value="authorization">Authorization</option>
                <option value="data_access">Data Access</option>
                <option value="data_modification">Data Modification</option>
                <option value="security">Security</option>
                <option value="system">System</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Severity</label>
              <select
                style={styles.filterInput}
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              >
                <option value="">All Severities</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Status</label>
              <select
                style={styles.filterInput}
                value={filters.success}
                onChange={(e) => setFilters({ ...filters, success: e.target.value })}
              >
                <option value="">All</option>
                <option value="true">Success</option>
                <option value="false">Failed</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>User Email</label>
              <input
                type="text"
                style={styles.filterInput}
                placeholder="Search email..."
                value={filters.userEmail}
                onChange={(e) => setFilters({ ...filters, userEmail: e.target.value })}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>IP Address</label>
              <input
                type="text"
                style={styles.filterInput}
                placeholder="Filter by IP..."
                value={filters.ipAddress}
                onChange={(e) => setFilters({ ...filters, ipAddress: e.target.value })}
              />
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              style={{ ...styles.button, ...styles.buttonPrimary }}
              onClick={() => { setPage(0); fetchLogs(); }}
            >
              Apply Filters
            </button>
            <button
              style={styles.button}
              onClick={() => {
                setFilters({
                  startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  endDate: new Date().toISOString().split('T')[0],
                  eventCategory: '',
                  severity: '',
                  success: '',
                  userEmail: '',
                  ipAddress: ''
                });
                setPage(0);
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Dashboard View */}
      {viewMode === 'dashboard' && summary && (
        <>
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{summary.total_events || 0}</div>
              <div style={styles.statLabel}>Total Events</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: '4px solid #ef4444' }}>
              <div style={{ ...styles.statValue, color: '#ef4444' }}>{summary.failed_events || 0}</div>
              <div style={styles.statLabel}>Failed Events</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{summary.unique_users || 0}</div>
              <div style={styles.statLabel}>Unique Users</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{summary.by_category?.authentication || 0}</div>
              <div style={styles.statLabel}>Auth Events</div>
            </div>
          </div>

          {/* Events by Category */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={styles.statCard}>
              <h3 style={{ fontWeight: '600', marginBottom: '1rem' }}>Events by Category</h3>
              {Object.entries(summary.by_category || {}).map(([category, count]) => (
                <div key={category} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ textTransform: 'capitalize' }}>{category.replace('_', ' ')}</span>
                  <span style={{ fontWeight: '600' }}>{count}</span>
                </div>
              ))}
            </div>
            <div style={styles.statCard}>
              <h3 style={{ fontWeight: '600', marginBottom: '1rem' }}>Top Failed IPs</h3>
              {(summary.top_failed_ips || []).length === 0 ? (
                <p style={{ color: '#64748b' }}>No failed attempts</p>
              ) : (
                summary.top_failed_ips.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontFamily: 'monospace' }}>{item.ip}</span>
                    <span style={{ fontWeight: '600', color: '#ef4444' }}>{item.count} failures</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Critical Events */}
          <div style={styles.statCard}>
            <h3 style={{ fontWeight: '600', marginBottom: '1rem' }}>Recent Critical Events</h3>
            {(summary.recent_critical || []).length === 0 ? (
              <p style={{ color: '#64748b' }}>No critical events in this period</p>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748b' }}>
                      <th style={{ padding: '0.5rem' }}>Time</th>
                      <th style={{ padding: '0.5rem' }}>Event</th>
                      <th style={{ padding: '0.5rem' }}>User</th>
                      <th style={{ padding: '0.5rem' }}>IP</th>
                      <th style={{ padding: '0.5rem' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.recent_critical.map((event) => (
                      <tr key={event.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.5rem' }}>{formatDate(event.timestamp)}</td>
                        <td style={{ padding: '0.5rem' }}>{event.event_type}</td>
                        <td style={{ padding: '0.5rem' }}>{event.user_email || '-'}</td>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{event.ip_address || '-'}</td>
                        <td style={{ padding: '0.5rem', color: '#ef4444' }}>{event.failure_reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Logs Table View */}
      {viewMode === 'logs' && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
              <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading audit logs...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>
              <AlertTriangle size={32} />
              <p style={{ marginTop: '1rem' }}>{error}</p>
            </div>
          ) : (
            <div style={styles.table}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Timestamp</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Event</th>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>IP Address</th>
                    <th style={styles.th}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#64748b' }}>
                        No audit logs found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedLog(log)}>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Calendar size={14} style={{ color: '#64748b' }} />
                            {formatDate(log.timestamp)}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, ...getCategoryColor(log.event_category) }}>
                            {log.event_category}
                          </span>
                        </td>
                        <td style={styles.td}>{log.event_type}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, ...getSeverityColor(log.severity), border: '1px solid' }}>
                            {getSeverityIcon(log.severity)}
                            {log.severity}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {log.success ? (
                            <CheckCircle size={18} style={{ color: '#22c55e' }} />
                          ) : (
                            <XCircle size={18} style={{ color: '#ef4444' }} />
                          )}
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <User size={14} style={{ color: '#64748b' }} />
                            {log.user_email || '-'}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            <Globe size={14} style={{ color: '#64748b' }} />
                            {log.ip_address || '-'}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <button
                            style={{ ...styles.button, padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={(e) => { e.stopPropagation(); setSelectedLog(log); }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              
              {/* Pagination */}
              <div style={styles.pagination}>
                <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total} logs
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    style={styles.button}
                    disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <button
                    style={styles.button}
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div style={styles.modal} onClick={() => setSelectedLog(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Event Details</h2>
              <button
                style={{ ...styles.button, padding: '0.25rem' }}
                onClick={() => setSelectedLog(null)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={styles.filterLabel}>Timestamp</div>
                <div>{formatDate(selectedLog.timestamp)}</div>
              </div>
              <div>
                <div style={styles.filterLabel}>Event ID</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{selectedLog.id}</div>
              </div>
              <div>
                <div style={styles.filterLabel}>Category</div>
                <span style={{ ...styles.badge, ...getCategoryColor(selectedLog.event_category) }}>
                  {selectedLog.event_category}
                </span>
              </div>
              <div>
                <div style={styles.filterLabel}>Event Type</div>
                <div>{selectedLog.event_type}</div>
              </div>
              <div>
                <div style={styles.filterLabel}>Severity</div>
                <span style={{ ...styles.badge, ...getSeverityColor(selectedLog.severity) }}>
                  {selectedLog.severity}
                </span>
              </div>
              <div>
                <div style={styles.filterLabel}>Status</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {selectedLog.success ? (
                    <><CheckCircle size={16} style={{ color: '#22c55e' }} /> Success</>
                  ) : (
                    <><XCircle size={16} style={{ color: '#ef4444' }} /> Failed</>
                  )}
                </div>
              </div>
              <div>
                <div style={styles.filterLabel}>User Email</div>
                <div>{selectedLog.user_email || '-'}</div>
              </div>
              <div>
                <div style={styles.filterLabel}>IP Address</div>
                <div style={{ fontFamily: 'monospace' }}>{selectedLog.ip_address || '-'}</div>
              </div>
              <div>
                <div style={styles.filterLabel}>User Agent</div>
                <div style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{selectedLog.user_agent || '-'}</div>
              </div>
              <div>
                <div style={styles.filterLabel}>Endpoint</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{selectedLog.endpoint || '-'}</div>
              </div>
              {selectedLog.failure_reason && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={styles.filterLabel}>Failure Reason</div>
                  <div style={{ color: '#ef4444' }}>{selectedLog.failure_reason}</div>
                </div>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={styles.filterLabel}>Metadata</div>
                  <pre style={{ 
                    backgroundColor: '#f8fafc', 
                    padding: '0.75rem', 
                    borderRadius: '0.5rem',
                    fontSize: '0.75rem',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SecurityAuditLogs;
